import { useState, useEffect } from "react";
import { X, FileCheck2, ShieldCheck, FileSignature } from "lucide-react";

/**
 * Scrollable liquid-glass FEYNLAB info modal.
 * Triggered from the Ceramic Coating service "More Info" button.
 */
const HIGHLIGHTS = [
  {
    icon: FileCheck2,
    title: "CARFAX Reporting",
    body: "Every FEYNLAB ceramic coating installation is registered with CARFAX — a verifiable record that adds tangible resale value to your vehicle for future buyers.",
  },
  {
    icon: ShieldCheck,
    title: "Warranty Backed By FEYNLAB",
    body: "Coatings are installed exclusively by certified technicians and come with a manufacturer-backed warranty covering performance and durability through years of Canadian conditions.",
  },
  {
    icon: FileSignature,
    title: "Insurance Claimable",
    body: "In the event of an accident, the ceramic coating system is claimable through your auto insurer — protecting both your finish and your investment.",
  },
];

export default function FeynlabModal({ open, onClose }) {
  const [mounted, setMounted] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => setShow(true));
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      const onKey = (e) => e.key === "Escape" && handleClose();
      window.addEventListener("keydown", onKey);
      return () => {
        cancelAnimationFrame(id);
        document.body.style.overflow = prev;
        window.removeEventListener("keydown", onKey);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setShow(false);
    setTimeout(() => {
      setMounted(false);
      onClose();
    }, 240);
  };

  if (!mounted || !open) return null;

  return (
    <div
      data-testid="feynlab-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
      style={{
        opacity: show ? 1 : 0,
        transition: "opacity 240ms ease-out",
      }}
    >
      {/* Backdrop */}
      <button
        aria-label="Close"
        onClick={handleClose}
        className="absolute inset-0 cursor-default bg-black/40"
        style={{
          backdropFilter: "blur(10px) saturate(110%)",
          WebkitBackdropFilter: "blur(10px) saturate(110%)",
        }}
      />

      {/* Glass card — scrollable */}
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-lg max-h-[88vh] rounded-[32px] overflow-hidden flex flex-col"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.10) 100%)",
          backdropFilter: "blur(32px) saturate(180%)",
          WebkitBackdropFilter: "blur(32px) saturate(180%)",
          border: "1px solid rgba(255,255,255,0.22)",
          boxShadow:
            "0 32px 100px -20px rgba(0,0,0,0.55), 0 6px 24px -6px rgba(0,0,0,0.4), inset 0 1.5px 0 rgba(255,255,255,0.55), inset 0 -1px 0 rgba(255,255,255,0.06)",
          opacity: show ? 1 : 0,
          transition: "opacity 320ms cubic-bezier(0.16, 1, 0.3, 1)",
          isolation: "isolate",
        }}
      >
        {/* Glare highlight */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[32px]"
          style={{
            background:
              "linear-gradient(130deg, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.10) 16%, rgba(255,255,255,0) 32%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.08) 88%, rgba(255,255,255,0.22) 100%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-8 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.9) 50%, transparent 100%)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -left-16 w-72 h-72 rounded-full"
          style={{
            background: "rgba(255,255,255,0.18)",
            filter: "blur(60px)",
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-20 -right-10 w-80 h-80 rounded-full"
          style={{
            background: "rgba(212,175,55,0.22)",
            filter: "blur(70px)",
          }}
        />

        {/* Close */}
        <button
          data-testid="feynlab-modal-close"
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-5 right-5 z-20 w-10 h-10 rounded-full flex items-center justify-center text-white"
          style={{
            border: "1px solid rgba(255,255,255,0.28)",
            background:
              "linear-gradient(140deg, rgba(255,255,255,0.22), rgba(255,255,255,0.06))",
            boxShadow:
              "inset 0 1px 0 rgba(255,255,255,0.55), 0 4px 12px rgba(0,0,0,0.25)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          <X size={16} strokeWidth={2} />
        </button>

        {/* Scrollable content */}
        <div className="relative overflow-y-auto overscroll-contain p-8 sm:p-10 pr-6 sm:pr-10">
          {/* FEYNLAB wordmark (pure white, text-only — not the official logo) */}
          <div className="mb-3">
            <span
              className="inline-block font-display font-extrabold text-3xl sm:text-[34px] tracking-[-0.04em] text-white"
              style={{ textShadow: "0 1px 12px rgba(0,0,0,0.5)" }}
            >
              FEYNLAB
              <sup className="text-xs align-super ml-0.5 text-white/70">®</sup>
            </span>
          </div>
          <div className="mb-6">
            <span className="inline-block text-[10px] uppercase tracking-[0.28em] text-white/60 border border-white/15 rounded-full px-2.5 py-1">
              Certified Installer
            </span>
          </div>

          <h3
            className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white leading-tight"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.45)" }}
          >
            The gold standard in automotive ceramic coating.
          </h3>

          <div
            className="mt-5 space-y-4 text-[15px] sm:text-base leading-relaxed text-white/90"
            style={{ textShadow: "0 1px 6px rgba(0,0,0,0.4)" }}
          >
            <p>
              FEYNLAB® is a global pioneer in protective ceramic coating
              technology — engineered in America, independently tested, and
              consistently outperforming competitors in third-party reviews.
              From the first nano-coating to the world&apos;s first
              self-healing ceramic system, FEYNLAB continues to define what
              premium paint protection means.
            </p>
            <p>
              Built for the realities of Canadian roads, FEYNLAB coatings deliver
              extreme chemical resistance, intense UV protection, lasting deep
              gloss, and a hydrophobic surface that makes routine maintenance
              fast and effortless. The result: paint that stays clearer, deeper
              and protected — season after season.
            </p>
            <p>
              ONE YORK is an authorized FEYNLAB Certified Installer — every
              coating is applied by trained technicians using the genuine
              FEYNLAB system, then registered and warrantied directly with the
              manufacturer.
            </p>
          </div>

          {/* Benefits */}
          <div className="mt-9 pt-6 border-t border-white/15">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#f4d774] font-medium mb-5">
              Extra Benefits You Get
            </div>
            <div className="space-y-3.5">
              {HIGHLIGHTS.map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl p-5 flex items-start gap-4"
                  style={{
                    border: "1px solid rgba(255,255,255,0.15)",
                    background:
                      "linear-gradient(140deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.06) 100%)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.30), 0 4px 14px -4px rgba(0,0,0,0.4)",
                  }}
                >
                  <div className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center shrink-0 text-[#f4d774]">
                    <Icon size={18} strokeWidth={1.6} />
                  </div>
                  <div>
                    <h4 className="font-display font-semibold text-base sm:text-lg text-white tracking-tight">
                      {title}
                    </h4>
                    <p className="mt-1.5 text-[13.5px] text-white/80 leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
